import { createInertiaApp } from '@inertiajs/react'
import { createRoot } from 'react-dom/client'
import { StrictMode } from 'react'
import { RiffrecProvider } from 'riffrec'
import { LocaleSync } from '~/components/locale_sync'
import { setLocale } from '~/lib/i18n'
import './application.css'

type PageModule = { default: React.ComponentType }

const pages = import.meta.glob<PageModule>(['../pages/**/*.tsx', '!../pages/**/*.test.tsx'])

createInertiaApp({
  resolve: async (name) => {
    const page = pages[`../pages/${name}.tsx`]
    if (!page) throw new Error(`Unknown Inertia page: ${name}`)
    return (await page()).default
  },
  setup({ el, App, props }) {
    // The server-resolved UI locale (InertiaController's shared prop) picks
    // the t() dictionary BEFORE the first render — the inertia_rails i18n
    // pattern, so there is never an English flash on a Dutch device.
    // LocaleSync keeps it current across client-side visits.
    setLocale((props.initialPage.props as { locale?: string }).locale)
    // RiffrecProvider holds the capture session ABOVE the page tree so a
    // recording survives Inertia navigations; the parent panel's
    // FeedbackRecorder (per page) only starts and stops it. Its first render
    // is a transparent context passthrough — no DOM, no window reads.
    // `forceEnable` inverts the package's disabled-in-production default
    // deliberately: the recorder renders only for a signed-in parent (the
    // server-shared `feedback_recorder_enabled` gate), and production is
    // exactly what the feedback lane exists to record.
    createRoot(el).render(
      <StrictMode>
        <RiffrecProvider forceEnable>
          <LocaleSync />
          <App {...props} />
        </RiffrecProvider>
      </StrictMode>
    )
  },
})
